import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { io } from 'socket.io-client';
import { API_URL, SOCKET_URL } from '../config';
import './DataPage.css';

const statusConfig = {
  pendente:     { label: 'Pendente',    color: '#f59e0b', bg: '#fef3c7' },
  confirmado:   { label: 'Confirmado',  color: '#3b82f6', bg: '#dbeafe' },
  inserido_sql: { label: 'Inserido',    color: '#10b981', bg: '#d1fae5' },
  cancelado:    { label: 'Cancelado',   color: '#6b7280', bg: '#f3f4f6' },
};

const origemConfig = {
  frontend:       { label: 'Sistema',  color: '#6366f1', bg: '#e0e7ff' },
  whatsapp:       { label: 'WhatsApp', color: '#22c55e', bg: '#dcfce7' },
  pagina_publica: { label: 'Link',     color: '#f97316', bg: '#ffedd5' },
};

function AgendamentosColeta() {
  const [agendamentos, setAgendamentos] = useState([]);
  const [stats, setStats] = useState({ pendente: 0, confirmado: 0, inserido_sql: 0, cancelado: 0 });
  const [loading, setLoading] = useState(true);

  // Filtros
  const [filtroStatus, setFiltroStatus] = useState('');
  const [filtroOrigem, setFiltroOrigem] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [dataInicio, setDataInicio] = useState('');
  const [dataFim, setDataFim] = useState('');

  // Modais
  const [modalNovo, setModalNovo] = useState(false);
  const [modalToken, setModalToken] = useState(false);
  const [modalCancelar, setModalCancelar] = useState(null);
  const [modalDetalhe, setModalDetalhe] = useState(null);
  const [tokenGerado, setTokenGerado] = useState(null);
  const [tokenCopiado, setTokenCopiado] = useState(false);

  // Anexos (modal detalhe)
  const [anexos, setAnexos] = useState([]);
  const [anexosLoading, setAnexosLoading] = useState(false);
  const [uploadingAnexo, setUploadingAnexo] = useState(false);
  const anexoInputRef = useRef(null);

  // Form nova solicitação
  const [form, setForm] = useState({
    nome: '', cpf: '', telefone: '', endereco: '',
    data_preferida: '', hora_preferida: '', convenio: '',
    plano: '', medico_solicitante: '', observacoes: '',
  });
  const [motivoCancelamento, setMotivoCancelamento] = useState('');
  const [salvando, setSalvando] = useState(false);

  // Socket.IO ref
  const socketRef = useRef(null);
  const fetchRef = useRef(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filtroStatus) params.set('status', filtroStatus);
      if (filtroOrigem) params.set('origem', filtroOrigem);
      if (search) params.set('search', search);
      if (dataInicio) params.set('dataInicio', dataInicio);
      if (dataFim) params.set('dataFim', dataFim);

      const { data } = await axios.get(`${API_URL}/agendamentos-coleta?${params}`);
      setAgendamentos(data.data || []);
      setStats(data.stats || {});
    } catch (err) {
      console.error('Erro ao buscar agendamentos:', err);
    } finally {
      setLoading(false);
    }
  }, [filtroStatus, filtroOrigem, search, dataInicio, dataFim]);

  // Guardar ref da fetchData para o socket usar sem dependência circular
  useEffect(() => { fetchRef.current = fetchData; }, [fetchData]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Socket.IO - real-time updates
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;

    const socket = io(SOCKET_URL, {
      auth: { token },
      transports: ['websocket', 'polling'],
    });

    socket.on('agendamento:novo', () => {
      if (fetchRef.current) fetchRef.current();
    });
    socket.on('agendamento:atualizado', () => {
      if (fetchRef.current) fetchRef.current();
    });

    socketRef.current = socket;

    return () => {
      socket.disconnect();
      socketRef.current = null;
    };
  }, []);

  // Buscar anexos ao abrir modal detalhe
  const fetchAnexos = useCallback(async (agendamentoId) => {
    setAnexosLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/agendamentos-coleta/${agendamentoId}/anexos`);
      setAnexos(data);
    } catch (err) {
      console.error('Erro ao buscar anexos:', err);
      setAnexos([]);
    } finally {
      setAnexosLoading(false);
    }
  }, []);

  useEffect(() => {
    if (modalDetalhe) {
      fetchAnexos(modalDetalhe.id);
    } else {
      setAnexos([]);
    }
  }, [modalDetalhe, fetchAnexos]);

  const handleSearch = (e) => {
    if (e.key === 'Enter') setSearch(searchInput);
  };

  const handleCriar = async (e) => {
    e.preventDefault();
    if (!form.nome || !form.telefone || !form.endereco || !form.data_preferida || !form.hora_preferida) {
      alert('Preencha os campos obrigatórios: Nome, Telefone, Endereço, Data e Hora');
      return;
    }
    setSalvando(true);
    try {
      await axios.post(`${API_URL}/agendamentos-coleta`, form);
      setModalNovo(false);
      setForm({ nome: '', cpf: '', telefone: '', endereco: '', data_preferida: '', hora_preferida: '', convenio: '', plano: '', medico_solicitante: '', observacoes: '' });
      fetchData();
    } catch (err) {
      alert('Erro ao criar solicitação: ' + (err.response?.data?.error || err.message));
    } finally {
      setSalvando(false);
    }
  };

  const handleAprovar = async (id) => {
    if (!window.confirm('Confirmar aprovação desta solicitação?')) return;
    try {
      await axios.put(`${API_URL}/agendamentos-coleta/${id}/aprovar`);
      fetchData();
    } catch (err) {
      alert('Erro ao aprovar: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleCancelar = async () => {
    if (!modalCancelar) return;
    try {
      await axios.put(`${API_URL}/agendamentos-coleta/${modalCancelar}/cancelar`, { motivo: motivoCancelamento });
      setModalCancelar(null);
      setMotivoCancelamento('');
      fetchData();
    } catch (err) {
      alert('Erro ao cancelar: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleInserirSQL = async (id) => {
    if (!window.confirm('Inserir esta coleta no sistema (SQL Server)? Esta ação não pode ser desfeita.')) return;
    try {
      await axios.put(`${API_URL}/agendamentos-coleta/${id}/inserir-sql`);
      fetchData();
    } catch (err) {
      alert('Erro ao inserir no sistema: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleGerarToken = async () => {
    try {
      const { data } = await axios.post(`${API_URL}/agendamentos-coleta/gerar-token`, { dias: 7 });
      const baseUrl = window.location.origin;
      setTokenGerado(`${baseUrl}${data.url}`);
      setTokenCopiado(false);
      setModalToken(true);
    } catch (err) {
      alert('Erro ao gerar link: ' + (err.response?.data?.error || err.message));
    }
  };

  const copiarLink = () => {
    if (tokenGerado) {
      navigator.clipboard.writeText(tokenGerado);
      setTokenCopiado(true);
      setTimeout(() => setTokenCopiado(false), 2000);
    }
  };

  // ─── Anexos handlers ───

  const handleUploadAnexo = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0 || !modalDetalhe) return;

    setUploadingAnexo(true);
    try {
      const formData = new FormData();
      for (const f of files) formData.append('anexos', f);
      await axios.post(`${API_URL}/agendamentos-coleta/${modalDetalhe.id}/anexos`, formData);
      fetchAnexos(modalDetalhe.id);
      fetchData(); // atualizar contagem na tabela
    } catch (err) {
      alert('Erro ao enviar anexo: ' + (err.response?.data?.error || err.message));
    } finally {
      setUploadingAnexo(false);
      if (anexoInputRef.current) anexoInputRef.current.value = '';
    }
  };

  const handleDeleteAnexo = async (anexoId) => {
    if (!window.confirm('Excluir este anexo?')) return;
    try {
      await axios.delete(`${API_URL}/agendamentos-coleta/anexo/${anexoId}`);
      fetchAnexos(modalDetalhe.id);
      fetchData();
    } catch (err) {
      alert('Erro ao excluir: ' + (err.response?.data?.error || err.message));
    }
  };

  const formatDate = (d) => {
    if (!d) return '-';
    return new Date(d).toLocaleDateString('pt-BR');
  };

  const formatDateTime = (d) => {
    if (!d) return '-';
    return new Date(d).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const styles = {
    pageHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '12px' },
    pageTitle: { fontSize: '1.5rem', fontWeight: '700', color: '#1a1a2e', margin: 0 },
    pageSubtitle: { fontSize: '0.85rem', color: '#666', margin: '2px 0 0' },
    statsRow: { display: 'flex', gap: '12px', marginBottom: '16px', flexWrap: 'wrap' },
    statCard: (color, bg) => ({ padding: '12px 20px', borderRadius: '8px', backgroundColor: bg, borderLeft: `4px solid ${color}`, minWidth: '140px', cursor: 'pointer' }),
    statLabel: { fontSize: '0.75rem', color: '#666', fontWeight: '600', textTransform: 'uppercase' },
    statValue: (color) => ({ fontSize: '1.5rem', fontWeight: '700', color, margin: '2px 0 0' }),
    badge: (color, bg) => ({ display: 'inline-block', padding: '2px 8px', borderRadius: '12px', fontSize: '0.75rem', fontWeight: '600', color, backgroundColor: bg }),
    btnPrimary: { padding: '8px 16px', backgroundColor: '#2563eb', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.85rem', fontWeight: '600' },
    btnSecondary: { padding: '8px 16px', backgroundColor: '#f3f4f6', color: '#374151', border: '1px solid #d1d5db', borderRadius: '6px', cursor: 'pointer', fontSize: '0.85rem', fontWeight: '600' },
    btnSuccess: { padding: '4px 10px', backgroundColor: '#10b981', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.78rem', fontWeight: '600' },
    btnDanger: { padding: '4px 10px', backgroundColor: '#ef4444', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.78rem', fontWeight: '600' },
    btnWarning: { padding: '4px 10px', backgroundColor: '#f59e0b', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.78rem', fontWeight: '600' },
    btnInfo: { padding: '4px 10px', backgroundColor: '#6366f1', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.78rem', fontWeight: '600' },
    overlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
    modal: { backgroundColor: '#fff', borderRadius: '12px', padding: '24px', width: '90%', maxWidth: '640px', maxHeight: '90vh', overflowY: 'auto' },
    formGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' },
    formGroup: { display: 'flex', flexDirection: 'column', gap: '4px' },
    label: { fontSize: '0.8rem', fontWeight: '600', color: '#374151' },
    input: { padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.9rem' },
    textarea: { padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.9rem', minHeight: '60px', resize: 'vertical' },
    // Detalhe modal
    detailGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' },
    detailItem: { display: 'flex', flexDirection: 'column', gap: '2px' },
    detailLabel: { fontSize: '0.7rem', fontWeight: '700', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em' },
    detailValue: { fontSize: '0.9rem', color: '#1f2937' },
    detailDivider: { borderTop: '1px solid #e5e7eb', margin: '12px 0' },
    // Anexos
    anexoGrid: { display: 'flex', flexWrap: 'wrap', gap: '10px', marginTop: '8px' },
    anexoItem: { position: 'relative', width: '90px', borderRadius: '6px', overflow: 'hidden', border: '1px solid #e5e7eb', backgroundColor: '#f9fafb' },
    anexoThumb: { width: '90px', height: '90px', objectFit: 'cover', display: 'block', cursor: 'pointer' },
    anexoPdf: { width: '90px', height: '90px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', gap: '2px' },
    anexoName: { fontSize: '0.6rem', color: '#6b7280', padding: '2px 4px', textAlign: 'center', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '90px' },
    anexoDelete: { position: 'absolute', top: '2px', right: '2px', width: '18px', height: '18px', borderRadius: '50%', backgroundColor: 'rgba(239,68,68,0.85)', color: '#fff', border: 'none', cursor: 'pointer', fontSize: '11px', lineHeight: '18px', textAlign: 'center', padding: 0 },
    clipIcon: { display: 'inline-block', marginLeft: '4px', fontSize: '0.8rem', color: '#6b7280', verticalAlign: 'middle' },
  };

  // Render detalhe field helper
  const DetailField = ({ label, value, full }) => (
    <div style={{ ...styles.detailItem, ...(full ? { gridColumn: '1 / -1' } : {}) }}>
      <span style={styles.detailLabel}>{label}</span>
      <span style={styles.detailValue}>{value || '-'}</span>
    </div>
  );

  const getAnexoUrl = (anexoId) => {
    const token = localStorage.getItem('token');
    return `${API_URL}/agendamentos-coleta/anexo/${anexoId}?token=${encodeURIComponent(token)}`;
  };

  return (
    <div className="data-page">
      {/* Header */}
      <div style={styles.pageHeader}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span style={{ fontSize: '1.8rem' }}>📋</span>
          <div>
            <h1 style={styles.pageTitle}>Solicitações de Coleta Domiciliar</h1>
            <p style={styles.pageSubtitle}>
              {agendamentos.length} solicitações
              {(search || filtroStatus || filtroOrigem || dataInicio || dataFim) && ' (filtrado)'}
            </p>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button style={styles.btnSecondary} onClick={handleGerarToken}>Gerar Link Público</button>
          <button style={styles.btnPrimary} onClick={() => setModalNovo(true)}>Nova Solicitação</button>
        </div>
      </div>

      {/* Stats - clicáveis para filtrar */}
      <div style={styles.statsRow}>
        {Object.entries(statusConfig).map(([key, cfg]) => (
          <div
            key={key}
            style={{ ...styles.statCard(cfg.color, cfg.bg), outline: filtroStatus === key ? `2px solid ${cfg.color}` : 'none' }}
            onClick={() => setFiltroStatus(filtroStatus === key ? '' : key)}
            title={`Filtrar por ${cfg.label}`}
          >
            <div style={styles.statLabel}>{cfg.label}</div>
            <div style={styles.statValue(cfg.color)}>{stats[key] || 0}</div>
          </div>
        ))}
      </div>

      {/* Filtros */}
      <div className="filters-section">
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
          <input
            type="text"
            placeholder="Buscar por nome, CPF ou telefone... (Enter)"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={handleSearch}
            style={{ flex: 1, minWidth: '200px', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.9rem' }}
          />
          <select value={filtroStatus} onChange={(e) => setFiltroStatus(e.target.value)} style={{ padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.85rem' }}>
            <option value="">Todos os status</option>
            {Object.entries(statusConfig).map(([key, cfg]) => (
              <option key={key} value={key}>{cfg.label}</option>
            ))}
          </select>
          <select value={filtroOrigem} onChange={(e) => setFiltroOrigem(e.target.value)} style={{ padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.85rem' }}>
            <option value="">Todas as origens</option>
            {Object.entries(origemConfig).map(([key, cfg]) => (
              <option key={key} value={key}>{cfg.label}</option>
            ))}
          </select>
          <input type="date" value={dataInicio} onChange={(e) => setDataInicio(e.target.value)} style={{ padding: '8px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.85rem' }} />
          <input type="date" value={dataFim} onChange={(e) => setDataFim(e.target.value)} style={{ padding: '8px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.85rem' }} />
          {(search || filtroStatus || filtroOrigem || dataInicio || dataFim) && (
            <button onClick={() => { setSearch(''); setSearchInput(''); setFiltroStatus(''); setFiltroOrigem(''); setDataInicio(''); setDataFim(''); }} style={{ ...styles.btnSecondary, padding: '8px 12px' }}>Limpar</button>
          )}
        </div>
      </div>

      {/* Tabela */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>Carregando...</div>
      ) : agendamentos.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>Nenhuma solicitação encontrada</div>
      ) : (
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Nome</th>
                <th>Telefone</th>
                <th>Endereço</th>
                <th>Data/Hora</th>
                <th>Convênio</th>
                <th>Origem</th>
                <th>Status</th>
                <th>Criado por</th>
                <th>Criado em</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {agendamentos.map((ag) => {
                const sc = statusConfig[ag.status] || statusConfig.pendente;
                const oc = origemConfig[ag.origem] || origemConfig.frontend;
                return (
                  <tr key={ag.id} style={{ cursor: 'pointer' }} onClick={() => setModalDetalhe(ag)}>
                    <td>
                      {ag.id}
                      {ag.total_anexos > 0 && <span style={styles.clipIcon} title={`${ag.total_anexos} anexo(s)`}>📎</span>}
                    </td>
                    <td style={{ fontWeight: '600' }}>{ag.nome || '-'}</td>
                    <td>{ag.telefone || '-'}</td>
                    <td style={{ maxWidth: '180px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={ag.endereco}>{ag.endereco || '-'}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>{formatDate(ag.data_preferida)} {ag.hora_preferida || ''}</td>
                    <td>{ag.convenio || '-'}</td>
                    <td><span style={styles.badge(oc.color, oc.bg)}>{oc.label}</span></td>
                    <td><span style={styles.badge(sc.color, sc.bg)}>{sc.label}</span></td>
                    <td style={{ fontSize: '0.8rem' }}>{ag.criado_por_nome || (ag.origem === 'whatsapp' ? ag.whatsapp_phone : ag.origem === 'pagina_publica' ? 'Paciente' : '-')}</td>
                    <td style={{ whiteSpace: 'nowrap', fontSize: '0.8rem' }}>{formatDateTime(ag.created_at)}</td>
                    <td style={{ whiteSpace: 'nowrap' }} onClick={(e) => e.stopPropagation()}>
                      <button style={styles.btnInfo} onClick={() => setModalDetalhe(ag)} title="Ver detalhes">Detalhes</button>{' '}
                      {ag.status === 'pendente' && (
                        <>
                          <button style={styles.btnSuccess} onClick={() => handleAprovar(ag.id)} title="Aprovar">Aprovar</button>{' '}
                          <button style={styles.btnDanger} onClick={() => setModalCancelar(ag.id)} title="Cancelar">Cancelar</button>
                        </>
                      )}
                      {ag.status === 'confirmado' && (
                        <>
                          <button style={styles.btnWarning} onClick={() => handleInserirSQL(ag.id)} title="Inserir no SQL Server">Inserir</button>{' '}
                          <button style={styles.btnDanger} onClick={() => setModalCancelar(ag.id)} title="Cancelar">Cancelar</button>
                        </>
                      )}
                      {ag.status === 'inserido_sql' && ag.coleta_domiciliar_id && (
                        <button style={styles.btnSecondary} onClick={() => window.open(`/coletas-domiciliares/${ag.coleta_domiciliar_id}`, '_blank')} title="Ver coleta no sistema">Ver SQL</button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal Detalhes */}
      {modalDetalhe && (() => {
        const ag = modalDetalhe;
        const sc = statusConfig[ag.status] || statusConfig.pendente;
        const oc = origemConfig[ag.origem] || origemConfig.frontend;
        return (
          <div style={styles.overlay} onClick={() => setModalDetalhe(null)}>
            <div style={{ ...styles.modal, maxWidth: '600px' }} onClick={(e) => e.stopPropagation()}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <h2 style={{ margin: 0, fontSize: '1.2rem', color: '#1a1a2e' }}>Solicitação #{ag.id}</h2>
                <div style={{ display: 'flex', gap: '6px' }}>
                  <span style={styles.badge(oc.color, oc.bg)}>{oc.label}</span>
                  <span style={styles.badge(sc.color, sc.bg)}>{sc.label}</span>
                </div>
              </div>

              {/* Dados do paciente */}
              <div style={{ fontSize: '0.8rem', fontWeight: '600', color: '#6b7280', marginBottom: '8px', textTransform: 'uppercase' }}>Dados do Paciente</div>
              <div style={styles.detailGrid}>
                <DetailField label="Nome" value={ag.nome} full />
                <DetailField label="CPF" value={ag.cpf} />
                <DetailField label="Telefone" value={ag.telefone} />
                <DetailField label="Endereço" value={ag.endereco} full />
              </div>

              <div style={styles.detailDivider} />

              {/* Dados da coleta */}
              <div style={{ fontSize: '0.8rem', fontWeight: '600', color: '#6b7280', marginBottom: '8px', textTransform: 'uppercase' }}>Dados da Coleta</div>
              <div style={styles.detailGrid}>
                <DetailField label="Data Preferida" value={formatDate(ag.data_preferida)} />
                <DetailField label="Hora Preferida" value={ag.hora_preferida} />
                <DetailField label="Convênio" value={ag.convenio} />
                <DetailField label="Plano" value={ag.plano} />
                <DetailField label="Médico Solicitante" value={ag.medico_solicitante} full />
                <DetailField label="Observações" value={ag.observacoes} full />
              </div>

              <div style={styles.detailDivider} />

              {/* Pedido Médico / Anexos */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <div style={{ fontSize: '0.8rem', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>
                  Pedido Médico / Anexos
                  {!anexosLoading && <span style={{ fontWeight: '400', marginLeft: '6px', color: '#9ca3af' }}>({anexos.length})</span>}
                </div>
                <div>
                  <input
                    ref={anexoInputRef}
                    type="file"
                    accept=".jpg,.jpeg,.png,.webp,.pdf"
                    multiple
                    style={{ display: 'none' }}
                    onChange={handleUploadAnexo}
                  />
                  <button
                    style={{ ...styles.btnSecondary, padding: '4px 10px', fontSize: '0.75rem' }}
                    onClick={() => anexoInputRef.current?.click()}
                    disabled={uploadingAnexo}
                  >
                    {uploadingAnexo ? 'Enviando...' : '+ Adicionar Anexo'}
                  </button>
                </div>
              </div>

              {anexosLoading ? (
                <div style={{ fontSize: '0.85rem', color: '#9ca3af', padding: '8px 0' }}>Carregando anexos...</div>
              ) : anexos.length === 0 ? (
                <div style={{ fontSize: '0.85rem', color: '#9ca3af', padding: '8px 0' }}>Nenhum anexo</div>
              ) : (
                <div style={styles.anexoGrid}>
                  {anexos.map((a) => {
                    const isImage = a.mime_type && a.mime_type.startsWith('image/');
                    const url = getAnexoUrl(a.id);
                    return (
                      <div key={a.id} style={styles.anexoItem}>
                        {isImage ? (
                          <img
                            src={url}
                            alt={a.file_name}
                            style={styles.anexoThumb}
                            onClick={() => window.open(url, '_blank')}
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                        ) : (
                          <div style={styles.anexoPdf} onClick={() => window.open(url, '_blank')}>
                            <span style={{ fontSize: '1.6rem' }}>📄</span>
                            <span style={{ fontSize: '0.65rem', color: '#6b7280' }}>PDF</span>
                          </div>
                        )}
                        <div style={styles.anexoName} title={a.file_name}>{a.file_name}</div>
                        <button
                          style={styles.anexoDelete}
                          onClick={(e) => { e.stopPropagation(); handleDeleteAnexo(a.id); }}
                          title="Excluir"
                        >&times;</button>
                      </div>
                    );
                  })}
                </div>
              )}

              <div style={styles.detailDivider} />

              {/* Histórico / Tracking */}
              <div style={{ fontSize: '0.8rem', fontWeight: '600', color: '#6b7280', marginBottom: '8px', textTransform: 'uppercase' }}>Histórico</div>
              <div style={styles.detailGrid}>
                <DetailField label="Criado por" value={ag.criado_por_nome || (ag.origem === 'whatsapp' ? `WhatsApp (${ag.whatsapp_phone})` : ag.origem === 'pagina_publica' ? 'Paciente (link público)' : '-')} />
                <DetailField label="Criado em" value={formatDateTime(ag.created_at)} />
                {ag.aprovado_por && (
                  <>
                    <DetailField label="Aprovado por" value={ag.aprovado_por_nome || `Usuário #${ag.aprovado_por}`} />
                    <DetailField label="Aprovado em" value={formatDateTime(ag.aprovado_em)} />
                  </>
                )}
                {ag.status === 'cancelado' && (
                  <>
                    <DetailField label="Cancelado em" value={formatDateTime(ag.cancelado_em)} />
                    <DetailField label="Motivo" value={ag.motivo_cancelamento} full />
                  </>
                )}
                {ag.status === 'inserido_sql' && (
                  <>
                    <DetailField label="Inserido no SQL por" value={`Usuário #${ag.inserido_sql_por}`} />
                    <DetailField label="Inserido em" value={formatDateTime(ag.inserido_sql_em)} />
                    <DetailField label="ID Coleta Domiciliar (SQL)" value={ag.coleta_domiciliar_id ? `#${ag.coleta_domiciliar_id}` : '-'} />
                  </>
                )}
                {ag.whatsapp_phone && (
                  <DetailField label="WhatsApp de Origem" value={ag.whatsapp_phone} />
                )}
              </div>

              {/* Ações no modal */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '20px', borderTop: '1px solid #e5e7eb', paddingTop: '16px' }}>
                <button style={styles.btnSecondary} onClick={() => setModalDetalhe(null)}>Fechar</button>
                {ag.status === 'pendente' && (
                  <>
                    <button style={{ ...styles.btnDanger, padding: '8px 16px' }} onClick={() => { setModalDetalhe(null); setModalCancelar(ag.id); }}>Cancelar</button>
                    <button style={{ ...styles.btnSuccess, padding: '8px 16px' }} onClick={() => { handleAprovar(ag.id); setModalDetalhe(null); }}>Aprovar</button>
                  </>
                )}
                {ag.status === 'confirmado' && (
                  <>
                    <button style={{ ...styles.btnDanger, padding: '8px 16px' }} onClick={() => { setModalDetalhe(null); setModalCancelar(ag.id); }}>Cancelar</button>
                    <button style={{ ...styles.btnWarning, padding: '8px 16px' }} onClick={() => { handleInserirSQL(ag.id); setModalDetalhe(null); }}>Inserir no Sistema</button>
                  </>
                )}
                {ag.status === 'inserido_sql' && ag.coleta_domiciliar_id && (
                  <button style={{ ...styles.btnPrimary }} onClick={() => window.open(`/coletas-domiciliares/${ag.coleta_domiciliar_id}`, '_blank')}>Ver no Sistema</button>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {/* Modal Nova Solicitação */}
      {modalNovo && (
        <div style={styles.overlay} onClick={() => setModalNovo(false)}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <h2 style={{ margin: '0 0 16px', fontSize: '1.2rem', color: '#1a1a2e' }}>Nova Solicitação de Coleta</h2>
            <form onSubmit={handleCriar}>
              <div style={styles.formGrid}>
                <div style={{ ...styles.formGroup, gridColumn: '1 / -1' }}>
                  <label style={styles.label}>Nome *</label>
                  <input style={styles.input} value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} required />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>CPF</label>
                  <input style={styles.input} value={form.cpf} onChange={(e) => setForm({ ...form, cpf: e.target.value })} />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Telefone *</label>
                  <input style={styles.input} value={form.telefone} onChange={(e) => setForm({ ...form, telefone: e.target.value })} required />
                </div>
                <div style={{ ...styles.formGroup, gridColumn: '1 / -1' }}>
                  <label style={styles.label}>Endereço *</label>
                  <input style={styles.input} value={form.endereco} onChange={(e) => setForm({ ...form, endereco: e.target.value })} required />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Data Preferida *</label>
                  <input type="date" style={styles.input} value={form.data_preferida} onChange={(e) => setForm({ ...form, data_preferida: e.target.value })} required />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Hora Preferida *</label>
                  <input type="time" style={styles.input} value={form.hora_preferida} onChange={(e) => setForm({ ...form, hora_preferida: e.target.value })} required />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Convênio</label>
                  <input style={styles.input} value={form.convenio} onChange={(e) => setForm({ ...form, convenio: e.target.value })} />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Plano</label>
                  <input style={styles.input} value={form.plano} onChange={(e) => setForm({ ...form, plano: e.target.value })} />
                </div>
                <div style={{ ...styles.formGroup, gridColumn: '1 / -1' }}>
                  <label style={styles.label}>Médico Solicitante</label>
                  <input style={styles.input} value={form.medico_solicitante} onChange={(e) => setForm({ ...form, medico_solicitante: e.target.value })} />
                </div>
                <div style={{ ...styles.formGroup, gridColumn: '1 / -1' }}>
                  <label style={styles.label}>Observações</label>
                  <textarea style={styles.textarea} value={form.observacoes} onChange={(e) => setForm({ ...form, observacoes: e.target.value })} />
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '16px' }}>
                <button type="button" style={styles.btnSecondary} onClick={() => setModalNovo(false)}>Cancelar</button>
                <button type="submit" style={styles.btnPrimary} disabled={salvando}>{salvando ? 'Salvando...' : 'Criar Solicitação'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Gerar Link */}
      {modalToken && (
        <div style={styles.overlay} onClick={() => setModalToken(false)}>
          <div style={{ ...styles.modal, maxWidth: '480px' }} onClick={(e) => e.stopPropagation()}>
            <h2 style={{ margin: '0 0 16px', fontSize: '1.2rem', color: '#1a1a2e' }}>Link Público Gerado</h2>
            <p style={{ fontSize: '0.85rem', color: '#666', margin: '0 0 12px' }}>Compartilhe este link com o paciente. Válido por 7 dias.</p>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <input
                type="text"
                readOnly
                value={tokenGerado || ''}
                style={{ ...styles.input, flex: 1, backgroundColor: '#f9fafb', fontSize: '0.8rem' }}
                onClick={(e) => e.target.select()}
              />
              <button style={styles.btnPrimary} onClick={copiarLink}>
                {tokenCopiado ? 'Copiado!' : 'Copiar'}
              </button>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '16px' }}>
              <button style={styles.btnSecondary} onClick={() => setModalToken(false)}>Fechar</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Cancelar */}
      {modalCancelar && (
        <div style={styles.overlay} onClick={() => { setModalCancelar(null); setMotivoCancelamento(''); }}>
          <div style={{ ...styles.modal, maxWidth: '420px' }} onClick={(e) => e.stopPropagation()}>
            <h2 style={{ margin: '0 0 16px', fontSize: '1.2rem', color: '#1a1a2e' }}>Cancelar Solicitação</h2>
            <div style={styles.formGroup}>
              <label style={styles.label}>Motivo do cancelamento (opcional)</label>
              <textarea style={styles.textarea} value={motivoCancelamento} onChange={(e) => setMotivoCancelamento(e.target.value)} placeholder="Informe o motivo..." />
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '16px' }}>
              <button style={styles.btnSecondary} onClick={() => { setModalCancelar(null); setMotivoCancelamento(''); }}>Voltar</button>
              <button style={{ ...styles.btnDanger, padding: '8px 16px' }} onClick={handleCancelar}>Confirmar Cancelamento</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AgendamentosColeta;
